import { InterfaceTab, tabType } from '#/types/types';

export class PaneRenderer {
    private ircClient: any;

    constructor(ircClient: any) {
        this.ircClient = ircClient;
    }

    renderTabContent(tab: InterfaceTab): void {
        if (!tab) return;

        const existingPane = document.querySelector(`.irc-pane[data-tab-id="${tab.key}"]`);
        if (existingPane) return;

        const contentArea = document.getElementById('irc-content');
        if (!contentArea) return;

        const pane = document.createElement('div');
        pane.className = 'irc-pane';
        pane.dataset.tabId = tab.type + ':' + tab.name.toLowerCase();
        pane.style.display = 'none';

        switch (tab.type) {
            case tabType.SETTINGS:
                this.createSettingsPane(pane);
                break;
            case tabType.CHANNEL_LIST:
                this.createChannelListPane(pane);
                break;
            default:
                this.createChatPane(pane, tab);
                break;
        }

        contentArea.appendChild(pane);
    }

    removeTabContent(tab: InterfaceTab): void {
        const paneElement = document.querySelector(`.irc-pane[data-tab-id="${tab.key}"]`);
        if (paneElement) {
            paneElement.remove();
        }
    }

    private createSettingsPane(pane: HTMLElement): void {       
        const settings = this.ircClient.settings;
        pane.innerHTML = `
            <div class="irc-settings">
                <div class="irc-setting-group group-row">
                    <label for="irc-nickname">Nickname:</label>
                    <input type="text" id="irc-nickname" value="${settings.nickname}" maxlength="30" required>
                    <label for="irc-nickserv-password">NickServ Pass (Optional):</label>
                    <input type="password" id="irc-nickserv-password" value="${settings.nickservPassword || ''}" maxlength="50" placeholder="Password">
                </div>
                <div class="irc-setting-group">
                    <label for="irc-channels">Auto-Join Channels:</label>
                    <input type="text" id="irc-channels" value="${settings.channels.join(',')}" placeholder="channel1,#channel2">
                </div>
                <div class="irc-setting-group group-row">
                    <label for="irc-server" hidden>Server (WSS Only):</label>
                    <input type="text" id="irc-server" value="${settings.server}" placeholder="irc.example.com" required hidden>
                    <label for="irc-port" hidden>Port:</label>
                    <input type="number" id="irc-port" value="${settings.port}" min="1" max="65535" required hidden>
                    <button id="irc-connect" class="irc-button">Connect</button>
                    <button id="irc-disconnect" class="irc-button">Disconnect</button>
                </div>
            </div>
        `;

        this.addInputValidation();
    }

    private createChannelListPane(pane: HTMLElement): void {
        const channelListContent = document.createElement('div');
        channelListContent.className = 'irc-channel-list';
        channelListContent.innerHTML = `
            <div class="irc-channel-list-header">
                <h3>Channel List</h3>
                <button class="irc-button" id="channel-list-refresh">Refresh</button>
            </div>
            <div class="irc-channel-list-container">
            </div>
        `;
        pane.appendChild(channelListContent);

        const refreshButton = channelListContent.querySelector('#channel-list-refresh') as HTMLButtonElement;
        if (refreshButton) {
            refreshButton.addEventListener('click', () => {
                this.ircClient.connectionHandler.sendIRCCommand('LIST');
            });
        }
    }

    private createChatPane(pane: HTMLElement, tab: InterfaceTab): void {
        if (!tab) return;

        const chatArea = document.createElement('div');
        chatArea.className = 'irc-chat-area';

        const messagesContainer = document.createElement('div');
        messagesContainer.className = 'irc-messages-container';

        const messages = document.createElement('div');
        messages.className = 'irc-messages';
        messages.dataset.tabId = tab.type + ':' + tab.name.toLowerCase();

        const inputArea = document.createElement('div');
        inputArea.className = 'irc-input-area';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'irc-input';
        if (tab.type === tabType.STATUS) {
            input.placeholder = `Send server commands...`;
        } else {
            input.placeholder = `Message ${tab.name}...`;
        }

        input.dataset.tabId = tab.type + ':' + tab.name.toLowerCase();

        inputArea.appendChild(input);
        messagesContainer.appendChild(messages);
        messagesContainer.appendChild(inputArea);

        chatArea.appendChild(messagesContainer);

        if (tab.type === tabType.CHANNEL) {
            const userList = this.createUserList(tab);
            chatArea.appendChild(userList);
        }

        pane.appendChild(chatArea);
    }

    private createUserList(tab: InterfaceTab): HTMLElement {
        const userList = document.createElement('div');
        userList.className = 'irc-userlist';
        userList.dataset.tabId = tab.type + ':' + tab.name.toLowerCase();

        const resizeHandle = document.createElement('div');
        resizeHandle.className = 'irc-userlist-resize-handle';
        userList.appendChild(resizeHandle);

        const userListHeader = document.createElement('div');
        userListHeader.className = 'irc-userlist-header';
        userListHeader.textContent = 'Users';

        const userListContent = document.createElement('div');
        userListContent.className = 'irc-userlist-content';
        userListContent.dataset.tabId = tab.type + ':' + tab.name.toLowerCase();

        userList.appendChild(userListHeader);
        userList.appendChild(userListContent);

        this.addUserListResize(userList, resizeHandle);

        return userList;
    }

    private addInputValidation(): void {
        const nicknameInput = document.getElementById('irc-nickname') as HTMLInputElement;
        const serverInput = document.getElementById('irc-server') as HTMLInputElement;
        const portInput = document.getElementById('irc-port') as HTMLInputElement;
        const channelsInput = document.getElementById('irc-channels') as HTMLInputElement;

        nicknameInput?.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const value = target.value;
            target.value = value.replace(/[^a-zA-Z0-9\[\]\\`_\^\{\|\}-]/g, '');
        });

        serverInput?.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const value = target.value;
            if (value && !/^([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$|^(\d{1,3}\.){3}\d{1,3}$/.test(value)) {
                target.setCustomValidity('Please enter a valid server address');
            } else {
                target.setCustomValidity('');
            }
        });

        portInput?.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const value = parseInt(target.value);
            if (value < 1 || value > 65535) {
                target.setCustomValidity('Port must be between 1 and 65535');
            } else {
                target.setCustomValidity('');
            }
        });

        channelsInput?.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            const value = target.value;
            const channels = value.split(',').map(c => c.trim());
            const invalidChannels = channels.filter(c => c && !c.startsWith('#') && !/^[a-zA-Z0-9_-]+$/.test(c));
            if (invalidChannels.length > 0) {
                target.setCustomValidity('Channel names must be valid (alphanumeric, underscore, or hyphen)');
            } else {
                target.setCustomValidity('');
            }
        });
    }

    private addUserListResize(userList: HTMLElement, resizeHandle: HTMLElement): void {
        let isResizing = false;
        let startX = 0;
        let startWidth = 0;

        resizeHandle.addEventListener('mousedown', (e) => {
            isResizing = true;
            startX = e.clientX;
            startWidth = parseInt(document.defaultView!.getComputedStyle(userList).width, 10);

            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'ew-resize';

            e.preventDefault();
        });

        document.addEventListener('mousemove', (e) => {
            if (!isResizing) return;

            const diff = startX - e.clientX;
            const newWidth = Math.max(120, Math.min(400, startWidth + diff));

            userList.style.width = newWidth + 'px';
        });

        document.addEventListener('mouseup', () => {
            if (isResizing) {
                isResizing = false;
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            }
        });
    }
}